import { CHEVRON_WIDTH_RATIO, vectorM } from './navChevrons.ts';

type LngLat = [number, number];

const M_PER_DEG = 111320;
/** Chevrons whose notches are closer than this fraction of the spacing overlap. */
const CLOSE_RATIO = 0.7;
/** Max sideways shift per chevron, as a fraction of the spacing. */
const MAX_OFFSET_RATIO = 0.25;
const MAX_ITERATIONS = 30;

export type SideOffsetInput = {
  notch: LngLat;
  /** Shared endpoints tuple identifying the source line segment. */
  segment: [LngLat, LngLat];
  /** Point on the route line the chevron is placed at. */
  center: LngLat;
  /** Unit route direction in meters, [east, north]. */
  dir: [number, number];
  /** Continuous run the chevron belongs to, and its position in that run. */
  run: number;
  index: number;
};

export type SideOffset = {
  visible: boolean;
  /** Translation in meters, [east, north]. */
  offset: [number, number];
};

/** Approximate point-to-point distance in meters using the local east/north frame. */
const distanceM = (a: LngLat, b: LngLat) => Math.hypot(...vectorM(a, b));

/** Dot product used for projections and direction alignment. */
const dot = (a: [number, number], b: [number, number]) => a[0] * b[0] + a[1] * b[1];

/** Distance in meters to the finite segment, clamping projections to its endpoints. */
const distanceToSegmentM = (point: LngLat, [start, end]: [LngLat, LngLat]) => {
  const edge = vectorM(start, end);
  const offset = vectorM(start, point);
  const lengthSquared = dot(edge, edge);
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, dot(offset, edge) / lengthSquared));
  return Math.hypot(offset[0] - t * edge[0], offset[1] - t * edge[1]);
};

/** Translates a longitude/latitude point by an east/north offset in meters. */
export const shiftLngLat = ([lng, lat]: LngLat, [east, north]: [number, number]): LngLat => [
  lng + east / (M_PER_DEG * Math.cos((lat * Math.PI) / 180)),
  lat + north / M_PER_DEG,
];

/** Consecutive chevrons on a run are regular spacing, unless the route doubles back (> 90° turn) between them. */
const isRegularNeighbour = (a: SideOffsetInput, b: SideOffsetInput) =>
  a.run === b.run && Math.abs(a.index - b.index) <= 1 && dot(a.dir, b.dir) >= 0;

/**
 * Separates overlapping chevrons on the same floor:
 * - Moves them sideways, up to the offset limit.
 * - Moves their neighbours half as far to smooth the route.
 * - Hides the earlier chevron if an overlap remains.
 * @param chevrons - Chevrons in route order, all on the same floor.
 * @param spacingM - Distance between chevrons at the current scale, in meters.
 * @returns Visibility and sideways offset for each input chevron.
 */
export const resolveChevronSideOffsets = (chevrons: SideOffsetInput[], spacingM: number): SideOffset[] => {
  const n = chevrons.length;
  const closeM = spacingM * CLOSE_RATIO;
  const laneWidthM = spacingM * CHEVRON_WIDTH_RATIO;
  const maxOffsetM = spacingM * MAX_OFFSET_RATIO;
  const normals = chevrons.map(({ dir }): [number, number] => [-dir[1], dir[0]]);
  // Signed shift of each chevron along its own left normal, in meters.
  const amounts = chevrons.map(() => 0);

  /** Applies a chevron's current signed lateral offset to a longitude/latitude point. */
  const shifted = (p: LngLat, i: number) =>
    shiftLngLat(p, [normals[i]![0] * amounts[i]!, normals[i]![1] * amounts[i]!]);
  /** Keeps each signed lateral shift within the per-chevron displacement budget. */
  const clamp = (v: number) => Math.max(-maxOffsetM, Math.min(maxOffsetM, v));
  /** Checks whether the shifted notch positions are within the overlap threshold. */
  const isClose = (i: number, j: number) =>
    distanceM(shifted(chevrons[i]!.notch, i), shifted(chevrons[j]!.notch, j)) < closeM;
  // Signed distance of i from j, across i's direction.
  const lateral = (i: number, j: number) =>
    dot(vectorM(shifted(chevrons[j]!.center, j), shifted(chevrons[i]!.center, i)), normals[i]!);

  const bySegment = new Map<SideOffsetInput['segment'], number[]>();
  chevrons.forEach(({ segment }, i) => {
    const indices = bySegment.get(segment);
    if (indices) {
      indices.push(i);
    } else {
      bySegment.set(segment, [i]);
    }
  });
  const segments = [...bySegment];
  const pairs: [number, number][] = [];
  for (let a = 0; a < segments.length; a++) {
    const [segmentA, indicesA] = segments[a]!;
    for (let b = a + 1; b < segments.length; b++) {
      const [segmentB, indicesB] = segments[b]!;
      if (
        !segmentA.some(point => distanceToSegmentM(point, segmentB) < spacingM) &&
        !segmentB.some(point => distanceToSegmentM(point, segmentA) < spacingM)
      ) {
        continue;
      }
      for (const i of indicesA) {
        for (const j of indicesB) {
          // Each chevron moves at most maxOffsetM; more distant pairs cannot overlap.
          if (
            !isRegularNeighbour(chevrons[i]!, chevrons[j]!) &&
            distanceM(chevrons[i]!.notch, chevrons[j]!.notch) < closeM + 2 * maxOffsetM
          ) {
            pairs.push([i, j]);
          }
        }
      }
    }
  }
  // Preserve route order for the solver and the newer-chevron hiding priority.
  pairs.sort(([i, j], [k, l]) => i - k || j - l);

  // Split each overlapping pair's lane deficit between the two, re-measuring from current positions until stable.
  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
    let moved = false;
    for (const [i, j] of pairs) {
      if (!isClose(i, j)) {
        continue;
      }
      const measured = lateral(i, j);
      // Coincident: i (older) goes to its own right, which sends j to its own right if opposite, left if same way.
      const lat = Math.abs(measured) < 1e-6 ? -1e-6 : measured;
      const deficit = laneWidthM - Math.abs(lat);
      if (deficit <= spacingM * 0.01) {
        continue;
      }
      const sign = Math.sign(lat);
      // j's normal only partly points across i's direction (e.g. at crossings); i makes up the rest.
      const share = dot(normals[i]!, normals[j]!);
      const [beforeI, beforeJ] = [amounts[i]!, amounts[j]!];
      amounts[j] = clamp(beforeJ + ((-sign * deficit) / 2) * (Math.sign(share) || 1));
      const jHelp = Math.abs((amounts[j]! - beforeJ) * share);
      amounts[i] = clamp(beforeI + sign * (deficit - jHelp));
      moved ||= amounts[i] !== beforeI || amounts[j] !== beforeJ;
    }
    if (!moved) {
      break;
    }
  }

  const ramped = amounts.map((amount, i) => {
    if (amount !== 0) {
      return amount;
    }
    const around = [i - 1, i + 1]
      .filter(j => j >= 0 && j < n && isRegularNeighbour(chevrons[i]!, chevrons[j]!))
      .map(j => amounts[j]!)
      .filter(v => v !== 0);
    if (!around.length || !around.every(v => Math.sign(v) === Math.sign(around[0]!))) {
      return 0;
    }
    return around.reduce((a, b) => (Math.abs(b) > Math.abs(a) ? b : a)) / 2;
  });
  ramped.forEach((v, i) => (amounts[i] = v));

  const results: SideOffset[] = chevrons.map((_, i) => ({
    visible: true,
    offset: amounts[i] ? [normals[i]![0] * amounts[i]!, normals[i]![1] * amounts[i]!] : [0, 0],
  }));

  // Pairs are sorted by (i, j), so walking backwards settles newer chevrons before the older ones they hide.
  const minLateralM = Math.min(laneWidthM, maxOffsetM * 2) * 0.95;
  for (let k = pairs.length - 1; k >= 0; k--) {
    const [i, j] = pairs[k]!;
    if (results[i]!.visible && results[j]!.visible && isClose(i, j) && Math.abs(lateral(i, j)) < minLateralM) {
      results[i]!.visible = false;
    }
  }
  return results;
};
